import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";

/* ── Brand tokens ── */
const COLOR = {
  surface: "oklch(var(--surface-subtle))",
  panel: "oklch(var(--surface-panel))",
  textStrong: "oklch(var(--foreground))",
  textBody: "oklch(0.38 0.012 70)",
  textMuted: "oklch(0.55 0.01 70)",
  textName: "oklch(0.25 0.01 70)",
  textTimestamp: "oklch(0.6 0.008 70)",
  accent: "oklch(var(--primary))",
  accentBg: "oklch(var(--primary) / 0.1)",
  border: "oklch(0.94 0.004 75)",
  panelShadow: "0 12px 32px -4px rgba(26, 28, 27, 0.06)",
} as const;

const EASE = [0.25, 0.1, 0.25, 1] as const;

const MAX_VISIBLE = 4;
// Each message row is ~52px (py-3 + content). 4 visible × 52 + py-2 padding
const MESSAGE_AREA_HEIGHT = 224;

interface Message {
  id: string;
  initial: string;
  name: string;
  bg: string;
  text: string;
  delay: number;
}

const MESSAGES: Message[] = [
  {
    id: "ravi",
    initial: "R",
    name: "Ravi",
    bg: "oklch(0.58 0.03 72)",
    text: "Did that invoice match the payment record? I need to reopen the PDF.",
    delay: 2200,
  },
  {
    id: "marta",
    initial: "M",
    name: "Marta",
    bg: "oklch(0.63 0.025 78)",
    text: "Which supplier contract renews this month? The signed file should say.",
    delay: 1800,
  },
  {
    id: "james",
    initial: "J",
    name: "James",
    bg: "oklch(0.56 0.018 68)",
    text: "Can you pull all vendor invoices from Q1? The accountant needs the source files.",
    delay: 2400,
  },
  {
    id: "sofia",
    initial: "S",
    name: "Sofia",
    bg: "oklch(0.61 0.032 64)",
    text: "Do we have a signed NDA with them? I need the latest contract record.",
    delay: 1600,
  },
  {
    id: "tom",
    initial: "T",
    name: "Tom",
    bg: "oklch(0.66 0.02 82)",
    text: "Which clients still owe us money right now?",
    delay: 2000,
  },
  {
    id: "aisha",
    initial: "A",
    name: "Aisha",
    bg: "oklch(0.59 0.022 58)",
    text: "Is that purchase order approved, or did the file never get updated?",
    delay: 1800,
  },
];

/* ── Avatar circle ── */
function Avatar({ initial, bg }: { initial: string; bg: string }) {
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: bg }}
      aria-hidden="true"
    >
      <span
        className="text-[11px] font-medium leading-none text-white uppercase"
        style={{ userSelect: "none" }}
      >
        {initial}
      </span>
    </div>
  );
}

/* ── Hash icon (SVG) ── */
function HashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0, color: "oklch(0.55 0.01 70)" }}
    >
      <path
        d="M2.5 5h9M2.5 9h9M5.5 2l-1 10M9.5 2l-1 10"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ── Single chat message row ── */
interface MessageRowProps {
  message: Message;
  age: number; // 0 = newest
  isNew: boolean;
  index: number; // position in MESSAGES array for alternating direction
}

function MessageRow({ message, age, isNew, index }: MessageRowProps) {
  // Age-based opacity: newest = 0.92, age 1 = 0.7, age 2 = 0.5, age 3 = 0.3
  const targetOpacity = age === 0 ? 0.92 : age === 1 ? 0.7 : age === 2 ? 0.5 : 0.3;
  // Alternate slide direction: even from left, odd from right
  const slideX = index % 2 === 0 ? -40 : 40;

  return (
    <motion.div
      key={message.id}
      layout
      initial={isNew ? { opacity: 0, x: slideX } : false}
      animate={{ opacity: targetOpacity, x: 0 }}
      exit={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
      transition={{
        opacity: {
          duration: isNew ? 0.5 : 0.35,
          ease: EASE,
        },
        x: {
          duration: 0.6,
          ease: EASE,
        },
        layout: {
          duration: 0.35,
          ease: EASE,
        },
      }}
      className="flex items-start gap-2.5 px-5 py-3"
    >
      <Avatar initial={message.initial} bg={message.bg} />
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span
            className="text-[13px] font-medium leading-none"
            style={{ color: COLOR.textName }}
          >
            {message.name}
          </span>
          <span
            className="text-[11px] leading-none flex-shrink-0"
            style={{ color: COLOR.textTimestamp }}
          >
            just now
          </span>
        </div>
        <p
          className="text-[13px] leading-relaxed font-light"
          style={{ color: COLOR.textBody }}
        >
          {message.text}
        </p>
      </div>
    </motion.div>
  );
}

/* ── Main component ── */
export default function SoundsFamiliar() {
  const [visibleMessages, setVisibleMessages] = useState<Message[]>([]);
  const [isDone, setIsDone] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  // Use refs to prevent re-triggering on scroll-out/back
  const hasStartedRef = useRef(false);
  const indexRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Schedules the next message appearance using per-message delay
  const scheduleNext = (currentIndex: number, messages: Message[]) => {
    if (currentIndex >= messages.length) return;

    const msg = messages[currentIndex];

    timeoutRef.current = setTimeout(() => {
      setVisibleMessages((prev) => {
        const next = [...prev, msg];
        // Keep only MAX_VISIBLE
        return next.slice(-MAX_VISIBLE);
      });

      const nextIndex = currentIndex + 1;
      indexRef.current = nextIndex;

      if (nextIndex >= messages.length) {
        setIsDone(true);
        // Badge fades in shortly after the last message settles
        setTimeout(() => setShowBadge(true), 600);
      } else {
        scheduleNext(nextIndex, messages);
      }
    }, msg.delay);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStartedRef.current) {
          hasStartedRef.current = true;
          scheduleNext(0, MESSAGES);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      className="px-6 lg:px-16 py-16 lg:py-24"
      style={{ background: COLOR.surface }}
    >
      <div className="max-w-7xl mx-auto">
        {/* ── Heading ── */}
        <motion.h2
          className="text-3xl lg:text-[2.75rem] font-semibold font-serif leading-tight mb-4 lg:mb-5 text-center"
          style={{ color: COLOR.textStrong }}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          viewport={{ once: true, amount: 0.3 }}
        >
          Your stack is missing a state layer
          <br />
          <span style={{ color: COLOR.accent }}>for documents.</span>
        </motion.h2>

        <motion.p
          className="text-sm lg:text-base font-light leading-relaxed text-center max-w-lg mx-auto mb-12 lg:mb-16"
          style={{ color: COLOR.textBody }}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.08 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          Contracts, invoices, forms, and files carry operational facts, but most systems only store the raw file.
          Lumient turns those documents into current records with source evidence attached.
        </motion.p>

        {/* ── Question Panel ── */}
        <motion.div
          ref={containerRef}
          className="max-w-xl mx-auto rounded-xl overflow-hidden"
          style={{
            background: COLOR.panel,
            boxShadow: COLOR.panelShadow,
            border: `1px solid ${COLOR.border}`,
          }}
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* ── Header bar ── */}
          <div
            className="flex items-center gap-2 px-5 py-3"
            style={{
              borderBottom: `1px solid ${COLOR.border}`,
            }}
          >
            <HashIcon />
            <span
              className="text-[13px] font-medium"
              style={{ color: COLOR.textMuted }}
            >
              document-ops
            </span>
            <span
              className="text-[13px] font-medium"
              style={{ color: COLOR.border }}
            >
              |
            </span>
            <span
              className="text-[13px] font-normal flex-1"
              style={{ color: COLOR.textMuted }}
            >
              Questions that should already have a record
            </span>

            {/* Unread badge — fades in once all messages have appeared */}
            <AnimatePresence>
              {showBadge && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="text-[11px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0"
                  style={{
                    color: COLOR.accent,
                    background: COLOR.accentBg,
                  }}
                >
                  6
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* ── Message area — fixed height prevents section reflow ── */}
          <div className="relative overflow-hidden" style={{ height: MESSAGE_AREA_HEIGHT }}>
            {/* Top gradient mask — softens messages exiting upward */}
            <div
              className="absolute inset-x-0 top-0 h-7 z-10 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, oklch(var(--surface-panel)) 0%, transparent 100%)",
              }}
            />

            {/* Messages anchored to bottom so new ones push upward naturally */}
            <div className="absolute inset-x-0 bottom-0 py-2">
              <AnimatePresence initial={false}>
                {visibleMessages.map((msg, i) => {
                  const age = visibleMessages.length - 1 - i; // 0 = newest
                  const isNew = i === visibleMessages.length - 1 && !isDone;
                  const originalIndex = MESSAGES.findIndex((m) => m.id === msg.id);
                  return (
                    <MessageRow
                      key={msg.id}
                      message={msg}
                      age={age}
                      isNew={isNew}
                      index={originalIndex}
                    />
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
